import React, { useMemo, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import InfoCard from "../../../components/ui/InfoCard";
import FileChip from "../../../components/ui/FileChip";
import {
  AccountsFields,
  AddressFields,
  AnnualRevenueField,
  BillingTermsFields,
  ClientPhoneField,
  ContactPersonField,
  EmailField,
  NameField,
  RegistrationCertificateField,
  RegistrationNumberField,
  TaxRegistrationField,
  WebsiteField,
} from "../../Client/CreateEditClient/CreateEditClientForm";
import { ADDRESS_FIELDS, EditPencil, SectionEditModal } from "../../Client/ClientSections";
import { childValidationSchema } from "../../Client/CreateEditClient";
import { updateCustomerProfileAsAdmin } from "../../../services/api/client";
import { showToast } from "../../../store/toast/toastSlice";
import {
  annualRevenueLabel,
  buildOwnCustomerFormData,
  formatPhoneNumber,
  getDefaultEntity,
  isSelfSignup,
  ownCustomerInitialValues,
  ownCustomerValidationSchema,
  pickFormData,
} from "../../../utils/helpers";

const validationSchema = ownCustomerValidationSchema.shape({
  companyAnnualRevenue: Yup.string().required("Annual Revenue is required"),
  accountsPerson: Yup.string().required("Accounts Person is required"),
  accountsEmail: Yup.string().required("Accounts Email is required").email("Invalid email format"),
  accountsPhone: Yup.string().required("Accounts Phone is required"),
  taxPercentage: childValidationSchema.fields.taxPercentage,
});

// Revenue is only asked at self-signup, and the contact person lives on the default entity.
const informationSection = ({ hasRevenue, hasContact }) => ({
  title: "Company Info",
  fields: [
    "clientName",
    ...(hasContact ? ["manager"] : []),
    "email",
    "phone",
    "website",
    ...(hasRevenue ? ["companyAnnualRevenue"] : []),
    ...ADDRESS_FIELDS,
  ],
  Fields: ({ ctx }) => (
    <>
      <NameField ctx={ctx} />
      {hasContact && <ContactPersonField ctx={ctx} />}
      <EmailField ctx={ctx} />
      <ClientPhoneField ctx={ctx} />
      <WebsiteField ctx={ctx} />
      {hasRevenue && <AnnualRevenueField ctx={ctx} />}
      <AddressFields ctx={ctx} />
    </>
  ),
});

const SECTIONS = {
  registration: {
    title: "Registration",
    fields: ["registrationNumber", "registrationCertificate", "taxRegistrationNumber"],
    Fields: ({ ctx }) => (
      <>
        <RegistrationNumberField ctx={ctx} />
        <RegistrationCertificateField ctx={ctx} />
        <TaxRegistrationField ctx={ctx} />
      </>
    ),
  },
  accounts: {
    title: "Accounts",
    fields: [
      "accountsPerson",
      "accountsEmail",
      "accountsPhone",
      "paymentTerms",
      "taxType",
      "taxPercentage",
    ],
    Fields: ({ ctx }) => (
      <>
        <AccountsFields ctx={ctx} />
        <BillingTermsFields ctx={ctx} />
      </>
    ),
  },
};

// Saved through the customer's profile route; registration and accounts go to its default entity.
const CustomerDetailsTab = ({ customer }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [editingSection, setEditingSection] = useState(null);

  const entity = getDefaultEntity(customer);
  const account = entity?.clientAccount;
  const selfSignup = isSelfSignup(customer);
  const hasEntity = !!entity;

  const sections = useMemo(
    () => ({
      ...SECTIONS,
      information: informationSection({ hasRevenue: selfSignup, hasContact: hasEntity }),
    }),
    [selfSignup, hasEntity],
  );

  const { isPending: isSaving, mutateAsync: saveProfile } = useMutation({
    mutationFn: updateCustomerProfileAsAdmin,
  });

  const section = sections[editingSection];

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...ownCustomerInitialValues(customer),
      companyAnnualRevenue: customer?.companyAnnualRevenue || "",
      accountsPerson: account?.accountsPerson || "",
      accountsEmail: account?.email || "",
      accountsPhone: account?.phone || "",
      paymentTerms: account?.paymentTerms ?? "",
      taxType: account?.taxType || "",
      taxPercentage: account?.taxPercentage ?? "",
    },
    validationSchema: section
      ? validationSchema.pick(section.fields.filter((field) => validationSchema.fields[field]))
      : undefined,
    onSubmit: async (values) => {
      try {
        const formData = buildOwnCustomerFormData(values);
        if (selfSignup) {
          formData.append("companyAnnualRevenue", values.companyAnnualRevenue || "");
        }
        formData.append("accountsPerson", values.accountsPerson || "");
        formData.append("accountsEmail", values.accountsEmail || "");
        formData.append("accountsPhone", values.accountsPhone || "");
        if (values.paymentTerms !== "") formData.append("paymentTerms", values.paymentTerms);
        formData.append("taxType", values.taxType || "");
        formData.append("taxPercentage", values.taxPercentage ?? "");

        await saveProfile({ id: customer.id, formData: pickFormData(formData, section?.fields) });
        dispatch(showToast({ type: "success", message: "Details updated successfully" }));
        await queryClient.invalidateQueries({ queryKey: ["client"] });
        setEditingSection(null);
      } catch (error) {
        dispatch(
          showToast({
            type: "error",
            message: error?.response?.data?.message || "Something went wrong. Please try again.",
          }),
        );
      }
    },
  });

  if (!customer) return null;

  const pencil = (key) => <EditPencil onClick={() => setEditingSection(key)} />;

  const sectionForm = {
    section,
    formik,
    isSaving,
    closeSection: () => {
      formik.resetForm();
      setEditingSection(null);
    },
    submitSection: formik.handleSubmit,
  };

  return (
    <>
      <InfoCard
        title="Company Info"
        actions={pencil("information")}
        items={[
          ["Company Name", customer.clientName],
          ["Contact Person", entity?.managerName],
          ["Email", customer.email],
          ["Phone", formatPhoneNumber(customer.phoneNumber)],
          ["Website", customer.website],
          ["Annual Revenue", annualRevenueLabel(customer.companyAnnualRevenue)],
          ["Address", customer.address?.addressLine1],
          ["City", customer.address?.city],
          ["State", customer.address?.state],
          ["Zip / Post Code", customer.address?.zipCode],
          ["Country", customer.address?.country],
        ]}
      />

      {selfSignup && (
        <>
          <InfoCard
            title="Registration"
            actions={pencil("registration")}
            items={[
              ["Registration Number", entity?.registrationNumber],
              ["Registration Certificate", <FileChip file={entity?.registrationCertificate} />],
              ["VAT/GST Registration", entity?.taxRegistrationNumber],
            ]}
          />
          <InfoCard
            title="Accounts"
            actions={pencil("accounts")}
            items={[
              ["Accounts Person", account?.accountsPerson],
              ["Accounts Email", account?.email],
              ["Accounts Phone", formatPhoneNumber(account?.phone)],
              ["Payment Terms", account?.paymentTerms],
              ["Tax Type", account?.taxType],
              [
                "Tax Percentage",
                account?.taxPercentage != null ? `${account.taxPercentage}%` : null,
              ],
            ]}
          />
        </>
      )}

      <SectionEditModal sectionForm={sectionForm} />
    </>
  );
};

export default CustomerDetailsTab;
